import React, { useState, useEffect } from "react";
import { crmColors } from "../theme/colors";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Avatar,
  InputAdornment,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import {
  School as SchoolIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  MenuBook as BookIcon,
  Edit as PencilIcon,
  DirectionsBus as BusIcon,
  Science as ScienceIcon,
  Calculate as MathIcon,
  Palette as ArtIcon,
  Computer as ComputerIcon,
  Psychology as PsychologyIcon,
  Language as LanguageIcon,
  Public as GeographyIcon,
  // EdTech and AI/ML Icons
  Code as CodeIcon,
  SmartToy as AIIcon,
  Psychology as MLIcon,
  Memory as MemoryIcon,
  CloudQueue as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  DataObject as DataIcon,
  Hub as NetworkIcon,
  AccountTree as TreeIcon,
  Timeline as TimelineIcon,
  Functions as FunctionsIcon,
  IntegrationInstructions as IntegrationIcon,
  AutoAwesome as AutoIcon,
  PrecisionManufacturing as ManufacturingIcon,
  RocketLaunch as RocketIcon,
  Diamond as DiamondIcon,
  Hexagon as HexagonIcon,
  Circle as CircleIcon,
  Square as SquareIcon,
  ChangeHistory as TriangleIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

// Animated Background Component
const AnimatedBackground = () => {
  const floatingIcons = [
    { Icon: BookIcon, delay: 0, duration: 8, x: 10, y: 20 },
    { Icon: PencilIcon, delay: 1, duration: 6, x: 85, y: 15 },
    { Icon: BusIcon, delay: 2, duration: 10, x: 20, y: 70 },
    { Icon: ScienceIcon, delay: 0.5, duration: 7, x: 80, y: 60 },
    { Icon: MathIcon, delay: 1.5, duration: 9, x: 15, y: 40 },
    { Icon: ArtIcon, delay: 3, duration: 8, x: 90, y: 30 },
    { Icon: ComputerIcon, delay: 0.8, duration: 9, x: 25, y: 10 },
    { Icon: PsychologyIcon, delay: 2.2, duration: 8, x: 70, y: 45 },
    { Icon: LanguageIcon, delay: 1.8, duration: 6, x: 40, y: 75 },
    { Icon: GeographyIcon, delay: 1.6, duration: 9, x: 85, y: 65 },
    // Additional icons for more visual richness
    { Icon: BookIcon, delay: 0.7, duration: 9, x: 45, y: 12 },
    { Icon: PencilIcon, delay: 1.3, duration: 7, x: 65, y: 85 },
    { Icon: BusIcon, delay: 2.1, duration: 11, x: 8, y: 35 },
    { Icon: ScienceIcon, delay: 0.9, duration: 8, x: 92, y: 45 },
    { Icon: MathIcon, delay: 1.7, duration: 10, x: 35, y: 65 },
    { Icon: ArtIcon, delay: 3.2, duration: 7, x: 55, y: 8 },
    { Icon: ComputerIcon, delay: 0.6, duration: 10, x: 50, y: 90 },
    { Icon: PsychologyIcon, delay: 2.4, duration: 7, x: 18, y: 15 },
    { Icon: LanguageIcon, delay: 1.9, duration: 9, x: 88, y: 75 },
    { Icon: GeographyIcon, delay: 1.7, duration: 8, x: 5, y: 45 },
    // Even more icons for a rich background
    { Icon: BookIcon, delay: 0.2, duration: 7, x: 95, y: 50 },
    { Icon: PencilIcon, delay: 1.1, duration: 8, x: 15, y: 25 },
    { Icon: BusIcon, delay: 2.3, duration: 9, x: 85, y: 5 },
    { Icon: ScienceIcon, delay: 0.8, duration: 6, x: 25, y: 80 },
    { Icon: MathIcon, delay: 1.6, duration: 11, x: 75, y: 40 },
    { Icon: ArtIcon, delay: 3.1, duration: 9, x: 35, y: 15 },
    { Icon: ComputerIcon, delay: 0.9, duration: 8, x: 58, y: 30 },
    { Icon: PsychologyIcon, delay: 2.5, duration: 10, x: 38, y: 85 },
    { Icon: LanguageIcon, delay: 1.3, duration: 9, x: 82, y: 20 },
    { Icon: GeographyIcon, delay: 1.4, duration: 9, x: 48, y: 5 },
    // Final set of icons for maximum visual impact
    { Icon: BookIcon, delay: 0.3, duration: 8, x: 95, y: 20 },
    { Icon: PencilIcon, delay: 1.8, duration: 9, x: 12, y: 70 },
    { Icon: BusIcon, delay: 2.8, duration: 7, x: 88, y: 85 },
    { Icon: ScienceIcon, delay: 0.1, duration: 10, x: 62, y: 12 },
    { Icon: MathIcon, delay: 1.2, duration: 8, x: 28, y: 90 },
    { Icon: ArtIcon, delay: 3.3, duration: 9, x: 95, y: 70 },
    { Icon: ComputerIcon, delay: 0.5, duration: 9, x: 45, y: 40 },
    { Icon: PsychologyIcon, delay: 2.3, duration: 8, x: 82, y: 10 },
    { Icon: LanguageIcon, delay: 1.0, duration: 7, x: 18, y: 40 },
    { Icon: GeographyIcon, delay: 1.1, duration: 10, x: 92, y: 60 },
    // EdTech and AI/ML Icons
    { Icon: CodeIcon, delay: 0.4, duration: 8, x: 15, y: 5 },
    { Icon: AIIcon, delay: 1.2, duration: 9, x: 85, y: 10 },
    { Icon: MLIcon, delay: 2.0, duration: 7, x: 5, y: 25 },
    { Icon: MemoryIcon, delay: 0.8, duration: 10, x: 95, y: 30 },
    { Icon: CloudIcon, delay: 1.5, duration: 8, x: 10, y: 50 },
    { Icon: SecurityIcon, delay: 2.3, duration: 9, x: 90, y: 55 },
    { Icon: SpeedIcon, delay: 0.6, duration: 7, x: 20, y: 75 },
    { Icon: AnalyticsIcon, delay: 1.8, duration: 8, x: 80, y: 80 },
    { Icon: DataIcon, delay: 2.5, duration: 9, x: 50, y: 5 },
    { Icon: NetworkIcon, delay: 0.9, duration: 10, x: 45, y: 95 },
    { Icon: TreeIcon, delay: 1.7, duration: 8, x: 75, y: 15 },
    { Icon: TimelineIcon, delay: 2.4, duration: 7, x: 25, y: 40 },
    { Icon: FunctionsIcon, delay: 0.3, duration: 9, x: 65, y: 25 },
    { Icon: IntegrationIcon, delay: 1.6, duration: 8, x: 35, y: 60 },
    { Icon: AutoIcon, delay: 2.2, duration: 10, x: 85, y: 40 },
    { Icon: ManufacturingIcon, delay: 0.7, duration: 7, x: 15, y: 90 },
    { Icon: RocketIcon, delay: 1.4, duration: 9, x: 95, y: 75 },
    // Geometric Shapes
    { Icon: DiamondIcon, delay: 2.1, duration: 8, x: 55, y: 35 },
    { Icon: HexagonIcon, delay: 0.5, duration: 9, x: 40, y: 70 },
    { Icon: CircleIcon, delay: 1.3, duration: 7, x: 70, y: 50 },
    { Icon: SquareIcon, delay: 2.6, duration: 8, x: 30, y: 15 },
    { Icon: TriangleIcon, delay: 0.2, duration: 10, x: 60, y: 85 },
    // Additional Tech Icons
    { Icon: CodeIcon, delay: 1.9, duration: 8, x: 8, y: 60 },
    { Icon: AIIcon, delay: 0.1, duration: 9, x: 92, y: 65 },
    { Icon: CloudIcon, delay: 2.7, duration: 7, x: 45, y: 20 },
    { Icon: AnalyticsIcon, delay: 1.1, duration: 10, x: 65, y: 70 },
    { Icon: DataIcon, delay: 0.4, duration: 8, x: 25, y: 85 },
    { Icon: NetworkIcon, delay: 1.8, duration: 9, x: 75, y: 35 },
    { Icon: RocketIcon, delay: 2.3, duration: 7, x: 12, y: 45 },
    { Icon: DiamondIcon, delay: 0.6, duration: 8, x: 88, y: 20 },
    { Icon: HexagonIcon, delay: 1.5, duration: 9, x: 35, y: 75 },
    { Icon: CircleIcon, delay: 2.4, duration: 8, x: 55, y: 10 },
    { Icon: SquareIcon, delay: 0.3, duration: 7, x: 85, y: 50 },
    { Icon: TriangleIcon, delay: 1.7, duration: 10, x: 15, y: 35 },
  ];

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: 0,
        background: `linear-gradient(135deg, ${crmColors.primary.dark} 0%, ${crmColors.primary.main} 50%, ${crmColors.primary.accent} 100%)`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(circle at 20% 50%, rgba(100, 149, 237, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(65, 105, 225, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(0, 35, 102, 0.4) 0%, transparent 50%)",
          animation: "gradientShift 15s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.05\"%3E%3Cpath d=\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')",
          opacity: 0.3,
        },
      }}
    >
      {/* Floating Icons */}
      {floatingIcons.map((item, index) => (
        <motion.div
          key={index}
          style={{
            position: "absolute",
            left: `${item.x}%`,
            top: `${item.y}%`,
            color: "rgba(255, 255, 255, 0.1)",
            zIndex: 1,
          }}
          animate={{
            y: [-20, 20, -20],
            rotate: [0, 360],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: item.duration,
            delay: item.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <item.Icon sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
        </motion.div>
      ))}

      {/* Floating Particles */}
      {[...Array(40)].map((_, index) => (
        <motion.div
          key={`particle-${index}`}
          style={{
            position: "absolute",
            width: "4px",
            height: "4px",
            background: "rgba(255, 255, 255, 0.6)",
            borderRadius: "50%",
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -100, 0],
            opacity: [0, 1, 0],
            scale: [0, 1, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 4,
            delay: Math.random() * 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* CSS Animation for gradient */}
      <style jsx>{`
        @keyframes gradientShift {
          0%,
          100% {
            transform: translateX(0) translateY(0);
          }
          25% {
            transform: translateX(20px) translateY(-10px);
          }
          50% {
            transform: translateX(-10px) translateY(20px);
          }
          75% {
            transform: translateX(10px) translateY(-5px);
          }
        }
      `}</style>
    </Box>
  );
};

const Login = () => {
  const theme = useTheme();
  const { login, isAuthenticated, getUserRole } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userId: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect to appropriate dashboard if already authenticated (only after successful login)
  // Note: This effect only runs if user is already authenticated when component mounts
  // It won't interfere with the login process
  useEffect(() => {
    // Only redirect if authenticated AND we have a valid user role
    // This prevents redirecting with stale/invalid auth data
    if (isAuthenticated) {
      const role = getUserRole();
      if (role) {
        const normalizedRole = role.toUpperCase();
        
        let dashboardPath = "/dashboard";
        if (normalizedRole === 'TL') {
          dashboardPath = "/dashboard/tl";
        } else if (normalizedRole === 'TRAINER') {
          dashboardPath = "/dashboard/trainer";
        }
        
        navigate(dashboardPath, { replace: true });
      }
    }
  }, [isAuthenticated, getUserRole, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await login(formData.userId, formData.password);
      if (result.success) {
        toast.success("Login successful!");
        
        // Navigate to role-based dashboard after successful login
        const userRole = result.user?.role || result.user?.loginType || 'ADMIN';
        const normalizedRole = userRole.toUpperCase();
        
        let dashboardPath = "/dashboard";
        if (normalizedRole === 'TL') {
          dashboardPath = "/dashboard/tl";
        } else if (normalizedRole === 'TRAINER') {
          dashboardPath = "/dashboard/trainer";
        }
        
        navigate(dashboardPath, { replace: true });
      } else {
        setError(result.error || "Login failed");
        toast.error("Login failed. Please try again.");
      }
    } catch (error) {
      setError("An unexpected error occurred");
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        p: 2,
        overflow: "hidden",
      }}
    >
      {/* Animated Background */}
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ position: "relative", zIndex: 10 }}
      >
        <Card
          sx={{
            width: { xs: "100%", sm: 400, md: 450 },
            maxWidth: { xs: "100%", sm: 400, md: 450 },
            minHeight: 550,
            borderRadius: 2,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1)",
            overflow: "hidden",
            backdropFilter: "blur(20px)",
            background: "rgba(255, 255, 255, 0.98)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.2)",
              transform: "translateY(-4px)",
            },
          }}
        >
          {/* Top Section - Visual/Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              p: 2,
              textAlign: "center",
              color: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Infographics Background */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0.1,
                zIndex: 1,
              }}
            >
              {/* Chart-like elements */}
              <Box
                sx={{
                  position: "absolute",
                  top: "20%",
                  left: "10%",
                  width: "60px",
                  height: "40px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "end",
                  justifyContent: "space-around",
                  p: 0.5,
                }}
              >
                {[20, 40, 60, 80, 100].map((height, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: "8px",
                      height: `${height}%`,
                      background: "rgba(255, 255, 255, 0.4)",
                      borderRadius: "2px",
                    }}
                  />
                ))}
              </Box>

              {/* Pie chart representation */}
              <Box
                sx={{
                  position: "absolute",
                  top: "15%",
                  right: "15%",
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                  background:
                    "conic-gradient(from 0deg, rgba(255,255,255,0.4) 0deg 120deg, rgba(255,255,255,0.2) 120deg 240deg, rgba(255,255,255,0.1) 240deg 360deg)",
                }}
              />

              {/* Network/connection lines */}
              <Box
                sx={{
                  position: "absolute",
                  top: "60%",
                  left: "5%",
                  width: "80%",
                  height: "2px",
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 20%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.3) 80%, transparent 100%)",
                }}
              />

              {/* Data points */}
              {[10, 30, 50, 70, 90].map((left, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "absolute",
                    top: "58%",
                    left: `${left}%`,
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.4)",
                  }}
                />
              ))}

              {/* Progress bars */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: "20%",
                  left: "10%",
                  width: "80%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {[60, 80, 40, 90].map((width, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: "100%",
                      height: "4px",
                      background: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${width}%`,
                        height: "100%",
                        background: "rgba(255, 255, 255, 0.3)",
                        borderRadius: "2px",
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Geometric shapes */}
              <Box
                sx={{
                  position: "absolute",
                  top: "25%",
                  right: "5%",
                  width: "30px",
                  height: "30px",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  transform: "rotate(45deg)",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  bottom: "30%",
                  right: "25%",
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                }}
              />
            </Box>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              style={{ position: "relative", zIndex: 2 }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: "auto",
                  mb: 2,
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <SchoolIcon sx={{ fontSize: 40 }} />
              </Avatar>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                EduOK CRM
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                School Management System
              </Typography>
            </motion.div>
          </Box>

          {/* Bottom Section - Form */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, textAlign: "center", fontWeight: 600 }}
              >
                Welcome Back
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  name="userId"
                  label="User ID"
                  type="text"
                  value={formData.userId}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: "0 4px 6px -1px rgba(65, 105, 225, 0.3), 0 2px 4px -1px rgba(65, 105, 225, 0.2)",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #001a4d 100%)`,
                        boxShadow: "0 10px 15px -3px rgba(65, 105, 225, 0.4), 0 4px 6px -2px rgba(65, 105, 225, 0.3)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {loading ? "Signing In..." : "Sign In"}
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          </Box>
        </Card>
      </motion.div>
    </Box>
  );
};

export default Login;
